import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { SettingsSection, StatusText, type StatusTone } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import type { FilterAst } from "../core/filter";
import { FilterField } from "../filters/FilterBuilder";
import { useFilterSources } from "../filters/useFilterSources";
import { useCoreDoc } from "./useCoreDoc";
import { useLevels } from "./levels";
import { Button, Checkbox, Field, Input, Select, Textarea, ConfirmButton, Fold } from "../ui";
import { useSession } from "./session";
import { apiCall } from "../core/api";
import { ButtonStepEditor } from "./ButtonStepEditor";
import { describeButton, stepBody, type ButtonInfo, type StepInfo } from "./buttonSteps";

interface Run {
  id: number;
  trigger: string;
  status: "running" | "ok" | "error" | "timeout";
  startedAt: string;
  finishedAt: string | null;
  stdout: string;
  stderr: string;
  error: string | null;
}

interface Automation {
  id: string;
  title: string;
  enabled: boolean;
  trigger: { kind: "cron" | "event" | "manual"; expr?: string; tz?: string; name?: string };
  action: { kind: string; id?: string; path?: string } | { kind: string; id?: string; path?: string }[];
  scopes: string[];
  toolName: string | null;
  lastRun?: Run;
}

interface Catalogue {
  actions: { id: string; scopes: string[] }[];
  events: string[];
}

const TONE: Record<Run["status"], StatusTone> = {
  running: "neutral",
  ok: "ok",
  error: "warn",
  timeout: "warn",
};

/**
 * Settings → Automations (spec §9.17, level `power`): what runs by itself, when it last ran and what it
 * printed. Definitions live in the workspace (they sync and go into bundles); runs are server logs.
 */
export function AutomationsSettings() {
  const { t, i18n } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const [items, setItems] = useState<Automation[]>([]);
  const [cat, setCat] = useState<Catalogue>({ actions: [], events: [] });
  const [open, setOpen] = useState<string | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const num = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(n);
  // a button is an automation that runs when pressed (UI cleanup U6): listed here, made in Buttons
  const [pressed, setPressed] = useState<ButtonInfo[]>([]);

  const refresh = useCallback(
    () =>
      workspaceId
        ? apiCall<{ automations: Automation[] }>(workspaceId, "GET", "/automations")
            .then((r) => setItems(r.automations))
            .catch(() => undefined)
        : Promise.resolve(),
    [workspaceId],
  );

  useEffect(() => {
    void refresh();
    if (workspaceId)
      void fetch(`/api/internal/buttons?workspace=${encodeURIComponent(workspaceId)}`, {
        credentials: "same-origin",
      })
        .then((r) => (r.ok ? (r.json() as Promise<{ buttons: ButtonInfo[] }>) : null))
        .then((d) => d && setPressed(d.buttons));
    if (workspaceId)
      void apiCall<Catalogue>(workspaceId, "GET", "/automations/actions")
        .then(setCat)
        .catch(() => undefined);
  }, [refresh, workspaceId]);

  const showRuns = useCallback(
    async (id: string) => {
      if (!workspaceId) return;
      setOpen(id);
      const r = await apiCall<{ runs: Run[] }>(workspaceId, "GET", `/automations/${id}/runs`).catch(
        () => null,
      );
      setRuns(r?.runs ?? []);
    },
    [workspaceId],
  );

  const fail = (e: unknown) =>
    setMessage(t(`errors.${(e as Error).message}`, { defaultValue: t("error.generic") }));

  const runNow = async (a: Automation) => {
    if (!workspaceId) return;
    setBusy(a.id);
    setMessage(null);
    try {
      const r = await apiCall<Run & { skipped?: boolean }>(
        workspaceId,
        "POST",
        `/automations/${a.id}/run`,
        {},
      );
      setMessage(r.skipped ? t("automations.skipped") : t(`automations.status.${r.status}`));
      await refresh();
      await showRuns(a.id);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (a: Automation, enabled: boolean) => {
    if (!workspaceId) return;
    await apiCall(workspaceId, "PATCH", `/automations/${a.id}`, { enabled }).catch(fail);
    await refresh();
  };

  const remove = async (a: Automation) => {
    if (!workspaceId) return;
    await apiCall(workspaceId, "DELETE", `/automations/${a.id}`).catch(fail);
    if (open === a.id) setOpen(null);
    await refresh();
  };

  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const when = (a: Automation) =>
    a.trigger.kind === "cron"
      ? t("automations.trigger.cronAt", { expr: a.trigger.expr, tz: a.trigger.tz ?? "UTC" })
      : a.trigger.kind === "event"
        ? t("automations.trigger.onEvent", { name: a.trigger.name })
        : t("automations.trigger.manual");
  const what = (a: Automation) =>
    (Array.isArray(a.action) ? a.action : [a.action])
      .map((x) => (x.kind === "script" ? x.path : x.id))
      .join(" → ");

  return (
    <SettingsSection
      id="settings-automations"
      intro={t("automations.hint")}
      summary={t("settings.summary.automations")}
      title={t("automations.title")}
      testId="settings-automations"
      folded
    >
      {items.length === 0 ? (
        <p className="text-[13px] text-neutral-500">{t("automations.none")}</p>
      ) : (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {items.map((a) => (
            <li key={a.id} className="flex flex-col gap-2 py-2" data-testid="automation-row">
              <div className="flex flex-wrap items-center gap-2">
                <Checkbox
                  checked={a.enabled}
                  label={t("automations.enabledLabel", { title: a.title })}
                  onChange={(v) => void toggle(a, v)}
                />
                <span className="text-[13.5px] font-medium">{a.title}</span>
                <span className="text-[12px] text-neutral-500">{when(a)}</span>
                <span className="font-mono text-[11.5px] text-neutral-500">{what(a)}</span>
                {a.toolName && <span className="font-mono text-[11px] text-neutral-500">{a.toolName}</span>}
                <span className="ml-auto flex items-center gap-2">
                  {a.lastRun && (
                    <StatusText tone={TONE[a.lastRun.status]}>
                      {t(`automations.status.${a.lastRun.status}`)} ·{" "}
                      {fmt.format(new Date(a.lastRun.startedAt))}
                    </StatusText>
                  )}
                  <Button
                    size="sm"
                    onClick={() => void runNow(a)}
                    disabled={busy === a.id}
                    data-testid="automation-run"
                  >
                    {busy === a.id ? t("automations.running") : t("automations.runNow")}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-expanded={open === a.id}
                    onClick={() => (open === a.id ? setOpen(null) : void showRuns(a.id))}
                  >
                    {t("automations.runs")}
                  </Button>
                  <ConfirmButton
                    size="sm"
                    variant="secondary"
                    aria-label={t("automations.deleteLabel", { title: a.title })}
                    question={t("automations.confirmDelete", { title: a.title })}
                    onConfirm={() => void remove(a)}
                  >
                    {t("automations.delete")}
                  </ConfirmButton>
                </span>
              </div>
              {open === a.id && (
                <div className="flex flex-col gap-2" data-testid="automation-runs">
                  {runs.length === 0 && (
                    <p className="text-[12.5px] text-neutral-500">{t("automations.noRuns")}</p>
                  )}
                  {runs.map((r) => (
                    <Fold
                      key={r.id}
                      className="rounded-md border border-neutral-200 p-2 dark:border-neutral-800"
                      label={
                        <>
                          {fmt.format(new Date(r.startedAt))} · {t(`automations.status.${r.status}`)} ·{" "}
                          {t(`automations.via.${r.trigger.split(":")[0]}`, { defaultValue: r.trigger })}
                          {r.error ? ` · ${r.error}` : ""}
                        </>
                      }
                    >
                      {r.stdout && (
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11.5px]">
                          {r.stdout}
                        </pre>
                      )}
                      {r.stderr && (
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11.5px] text-neutral-500">
                          {r.stderr}
                        </pre>
                      )}
                    </Fold>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {pressed.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="automation-pressed">
          <h3 className="text-[12.5px] text-neutral-500">{t("automations.pressed")}</h3>
          <ul className="flex flex-col gap-0.5">
            {pressed.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="font-medium">{b.label || describeButton(t, num, b)}</span>
                <span className="text-[12px] text-neutral-500">{t("automations.whenPressed")}</span>
              </li>
            ))}
          </ul>
          <a href="#settings-buttons" className="text-[12.5px] text-accent underline">
            {t("automations.pressedHint")}
          </a>
        </div>
      )}
      {message && (
        <p className="text-[12.5px]" role="status" data-testid="automation-message">
          {message}
        </p>
      )}
      {workspaceId && (
        <NewAutomation
          workspaceId={workspaceId}
          catalogue={cat}
          onCreated={() => void refresh()}
          onError={fail}
        />
      )}
    </SettingsSection>
  );
}

function NewAutomation({
  workspaceId,
  catalogue,
  onCreated,
  onError,
}: {
  workspaceId: string;
  catalogue: Catalogue;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const uid = useId();
  const [kind, setKind] = useState<"manual" | "cron" | "event">("manual");
  const [expr, setExpr] = useState("0 3 * * *");
  const [event, setEvent] = useState("group.completed");
  const [filter, setFilter] = useState<FilterAst | undefined>(undefined);
  const { handle: core } = useCoreDoc();
  const filterSources = useFilterSources(core, useLevels().settings);
  const [actionKind, setActionKind] = useState<"builtin" | "script">("builtin");
  const [builtin, setBuiltin] = useState("notify");
  const [path, setPath] = useState("");
  const [args, setArgs] = useState("{}");
  const [scopes, setScopes] = useState("");
  // the `step` action: the same step editor as Buttons (UI cleanup U6), in this workspace
  const [step, setStep] = useState<StepInfo | null>(null);
  const spaceName = useSession((s) => s.session?.workspaces?.find((w) => w.id === workspaceId)?.name ?? "");
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const isStep = actionKind === "builtin" && builtin === "step";
  const needed = [
    ...(catalogue.actions.find((a) => a.id === builtin)?.scopes ?? []),
    ...(isStep && step ? [stepScope(step.action)] : []),
  ];

  const create = async () => {
    let parsedArgs: unknown;
    let parsedFilter: unknown;
    try {
      parsedArgs = isStep ? stepArgs(step) : JSON.parse(args || "{}");
      if (isStep && !step) {
        onError(new Error("automations.stepIncomplete"));
        return;
      }
      parsedFilter = filter && Object.keys(filter).length ? filter : undefined;
    } catch {
      onError(new Error("automations.badJson"));
      return;
    }
    const trigger =
      kind === "cron"
        ? { kind, expr, tz }
        : kind === "event"
          ? { kind, name: event, ...(parsedFilter ? { filter: parsedFilter } : {}) }
          : { kind };
    const action =
      actionKind === "builtin"
        ? { kind: "builtin", id: builtin, args: parsedArgs }
        : { kind: "script", path, args: parsedArgs };
    const extra = scopes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await apiCall(workspaceId, "POST", "/automations", {
        title: title.trim(),
        trigger,
        action,
        scopes: Array.from(new Set([...(actionKind === "builtin" ? needed : []), ...extra])),
      });
      setTitle("");
      onCreated();
    } catch (e) {
      onError(e);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800"
      data-testid="automation-new"
    >
      <h3 className="text-[13px] font-medium">{t("automations.new")}</h3>
      <Field htmlFor={`${uid}-1`} label={t("automations.name")}>
        <Input
          id={`${uid}-1`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="automation-title"
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Field htmlFor={`${uid}-2`} label={t("automations.when")}>
          <Select
            id={`${uid}-2`}
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            data-testid="automation-trigger"
          >
            {(["manual", "cron", "event"] as const).map((k) => (
              <option key={k} value={k}>
                {t(`automations.kind.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        {kind === "cron" && (
          <Field htmlFor={`${uid}-3`} label={t("automations.cron", { tz })}>
            <Input
              id={`${uid}-3`}
              value={expr}
              onChange={(e) => setExpr(e.target.value)}
              className="font-mono"
            />
          </Field>
        )}
        {kind === "event" && (
          <>
            <Field htmlFor={`${uid}-4`} label={t("automations.event")}>
              <Select
                id={`${uid}-4`}
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                data-testid="automation-event"
              >
                {catalogue.events.map((ev) => (
                  <option key={ev} value={ev}>
                    {ev}
                  </option>
                ))}
              </Select>
            </Field>
            <Field htmlFor={`${uid}-5`} label={t("automations.filter")}>
              {filterSources && (
                <FilterField
                  id={`${uid}-5`}
                  value={filter}
                  onChange={setFilter}
                  sources={filterSources}
                  testId="automation-filter"
                />
              )}
            </Field>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Field htmlFor={`${uid}-6`} label={t("automations.do")}>
          <Select
            id={`${uid}-6`}
            value={actionKind}
            onChange={(e) => setActionKind(e.target.value as typeof actionKind)}
          >
            <option value="builtin">{t("automations.builtin")}</option>
            <option value="script">{t("automations.script")}</option>
          </Select>
        </Field>
        {actionKind === "builtin" ? (
          <Field htmlFor={`${uid}-7`} label={t("automations.action")}>
            <Select
              id={`${uid}-7`}
              value={builtin}
              onChange={(e) => setBuiltin(e.target.value)}
              data-testid="automation-action"
            >
              {catalogue.actions.map((a) => (
                <option key={a.id} value={a.id}>
                  {t(`automations.actions.${a.id}`, { defaultValue: a.id })}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field htmlFor={`${uid}-8`} label={t("automations.path")}>
            <Input
              id={`${uid}-8`}
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="weekly_report.py"
            />
          </Field>
        )}
      </div>
      {isStep ? (
        <ButtonStepEditor workspaces={[{ id: workspaceId, name: spaceName }]} home={workspaceId}>
          {(current) => <StepPicked current={current} onChange={setStep} />}
        </ButtonStepEditor>
      ) : (
        <Field htmlFor={`${uid}-9`} label={t("automations.args")}>
          <Textarea
            id={`${uid}-9`}
            value={args}
            onChange={(e) => setArgs(e.target.value)}
            rows={3}
            className="font-mono text-[12px]"
            data-testid="automation-args"
          />
        </Field>
      )}
      <Field htmlFor={`${uid}-10`} label={t("automations.scopes")}>
        <Input
          id={`${uid}-10`}
          value={scopes}
          onChange={(e) => setScopes(e.target.value)}
          placeholder={needed.join(", ") || "tasks:read"}
          className="font-mono"
        />
      </Field>
      {actionKind === "builtin" && needed.length > 0 && (
        <p className="text-[11.5px] text-neutral-500">
          {t("automations.scopesNeeded", { scopes: needed.join(", ") })}
        </p>
      )}
      <div>
        <Button onClick={() => void create()} disabled={!title.trim()} data-testid="automation-create">
          {t("automations.create")}
        </Button>
      </div>
    </div>
  );
}

/** Hands the step editor's current step to the form (the editor gives it to its children while rendering). */
function StepPicked({ current, onChange }: { current: StepInfo | null; onChange(s: StepInfo | null): void }) {
  const key = current ? JSON.stringify(stepBody(current)) : "";
  useEffect(() => onChange(current), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** A step as the `step` action's arguments: its workspace is the automation's own. */
function stepArgs(s: StepInfo | null): Record<string, unknown> {
  if (!s) return {};
  const { workspaceId: _ws, ...rest } = stepBody(s);
  return Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== null && v !== "" && v !== false));
}

/** The scope a step needs (as `drang.steps.scope_for`). */
function stepScope(action: string): string {
  if (/^(start|stop|timer|pomodoro)/.test(action)) return "tracking:write";
  if (action === "capture" || action === "addTask") return "capture:write";
  return "tasks:write";
}
